import { Box, Button, Divider, Group, Modal, Stack, Text } from '@mantine/core';
import { Eye, Lock, Users, Mic, Check } from 'react-feather';
import type { ReactNode } from 'react';

/**
 * Shared AI disclosure modal — role-aware.
 *
 * Introduced as the coach modal in MED-792 / 2.3a, generalized for the
 * participant waiting room (MED-804 / E3.2) and peer-led surfaces (E4.4).
 *
 * MED-1047 (E1.16b) — finalized copy + visual refresh. The reviewed copy
 * (ai-disclosure-copy-review.md) collapses to two templates: a **Leader**
 * template (coach + peer-leader facilitator) and a **Participant** template
 * (coach-led + peer-led participants, using the neutral label "session
 * leader"). The visual update moves the header glyph above a left-aligned
 * title, recolors the section icons teal, and places a single divider above
 * the footer (Figma 2582:13964).
 */

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export type AiDisclosureRole = 'coach' | 'participant' | 'peer_leader';

export interface AiDisclosureModalProps {
  opened: boolean;
  /** Which audience is reading the modal. Drives copy variants. */
  role: AiDisclosureRole;
  /** Primary CTA ("I understand — continue to session"). */
  onAcknowledge: () => void;
  /** Modal close (X / Escape) — returns the user to their pre-session page. */
  onDismiss: () => void;
  /**
   * Optional secondary CTA — when supplied, renders a "Return to portal"
   * button below the primary CTA. The participant template wires this; the
   * leader template omits it.
   */
  onReturnToPortal?: () => void;
}

interface DisclosureSectionProps {
  icon: ReactNode;
  title: string;
  bullets: string[];
}

// Figma 2582:13964 tokens.
const HEADER_ICON_BG = '#FEF3E2';
const HEADER_ICON_COLOR = '#E07A3B';
const SECTION_ICON_COLOR = '#2D7B77';
const TITLE_COLOR = '#1A1A2E';
const SUBTITLE_COLOR = '#6B7280';
const SECTION_TITLE_COLOR = '#374151';
const BULLET_TEXT_COLOR = '#374151';
const BULLET_DOT_COLOR = '#D1D5DB';
const DIVIDER_COLOR = '#F3F4F6';
const FOOTER_COLOR = '#9CA3AF';
const BUTTON_BG = '#1B2B3A';

// ---------------------------------------------------------------------------
// Finalized copy (MED-1047 — verbatim from ai-disclosure-copy-review.md).
// MED-1235 (E1.16c) updated the "How your data is handled" storage bullet in
// both templates to reflect transient transcript retention (MED-1212): the
// conversation is used to generate a post-session summary, then deleted.
// ---------------------------------------------------------------------------

interface RoleCopy {
  subtitle: string;
  whatItDoes: { title: string; bullets: string[] };
  whatLeaderSees: { title: string; bullets: string[] };
  howDataHandled: { title: string; bullets: string[] };
  footer: string;
}

// Neutral for every viewer (AC5) — no "your coach"/"your peer leader" phrasing.
const SUBTITLE =
  "AI listening has been enabled for today's session. Here's what that means for you.";

// Template A — Leader (coach or peer-leader facilitator).
const LEADER_COPY: RoleCopy = {
  subtitle: SUBTITLE,
  whatItDoes: {
    title: 'What AI listening does',
    bullets: [
      'Analyzes group discussion flow during the session',
      'Identifies key themes to support facilitation',
      'Generates topic and pacing suggestions to help you keep the session on track',
    ],
  },
  whatLeaderSees: {
    title: 'What the session leader sees',
    bullets: [
      'Real-time facilitation nudges based on discussion flow and topic',
      'Pacing alerts when a session step runs over time',
    ],
  },
  howDataHandled: {
    title: 'How your data is handled',
    bullets: [
      'During the session, short excerpts of the live conversation are sent to a secure AI service to generate suggestions',
      "This happens in real time and is not stored by the AI service, nor used to train AI models. Afterward it's used to generate a post-session summary, then automatically deleted. It is never viewable by Medley staff or shown anywhere in the app",
      "The AI works at the group level only; it doesn't score or evaluate individual participants",
    ],
  },
  footer:
    'AI listening cannot be opted out of once the session has been opened for participants to join. ' +
    'Depending on conversation, you will be able to pause listening to ensure everyone gets the most out of the session. ' +
    'Questions? Contact your program coordinator.',
};

// Template B — Participant (coach-led or peer-led). Neutral "session leader".
const PARTICIPANT_COPY: RoleCopy = {
  subtitle: SUBTITLE,
  whatItDoes: {
    title: 'What AI listening does',
    bullets: [
      'Analyzes group discussion flow during the session',
      'Identifies key themes to support facilitation',
      'Generates topic and pacing suggestions for your session leader to keep the session on track',
    ],
  },
  whatLeaderSees: {
    title: 'What your session leader sees',
    bullets: [
      'Real-time facilitation nudges based on discussion flow and topic',
      'Pacing alerts when a session step runs over time',
    ],
  },
  howDataHandled: {
    title: 'How your data is handled',
    bullets: [
      'During the session, short excerpts of the live conversation are sent to a secure AI service to generate facilitation suggestions for your session leader',
      "This happens in real time and is not stored by the AI service, nor used to train AI models. Afterward it's used to generate a post-session summary, then automatically deleted. It is never viewable by Medley staff or shown anywhere in the app",
      "The AI works at the group level only; it doesn't score or evaluate individual participants",
      'Your personal session notes are always private and are never sent to the AI',
      'Nothing the AI generates is shown to other participants',
    ],
  },
  footer:
    "AI listening can't be turned off for this session, but your session leader can pause it " +
    "during the conversation when it's helpful. Questions? Contact your program coordinator.",
};

// Two templates, three role values: coaches and peer-leader facilitators both
// render the Leader template (peer leaders pass role="coach" per MED-816);
// participants (coach-led or peer-led) render the Participant template.
const COPY_BY_ROLE: Record<AiDisclosureRole, RoleCopy> = {
  coach: LEADER_COPY,
  participant: PARTICIPANT_COPY,
  peer_leader: PARTICIPANT_COPY,
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function DisclosureSection({ icon, title, bullets }: DisclosureSectionProps) {
  return (
    <Stack gap={6}>
      <Group gap={8} align="center" wrap="nowrap">
        <Box style={{ color: SECTION_ICON_COLOR, lineHeight: 0 }}>{icon}</Box>
        <Text fw={700} fz={13} c={SECTION_TITLE_COLOR}>
          {title}
        </Text>
      </Group>
      <Stack gap={2} pl={24}>
        {bullets.map(b => (
          <Group key={b} gap={10} align="flex-start" wrap="nowrap">
            <Box
              aria-hidden="true"
              style={{
                width: 5,
                height: 5,
                borderRadius: 3,
                background: BULLET_DOT_COLOR,
                flexShrink: 0,
                marginTop: 7,
              }}
            />
            <Text fz={13} lh={1.6} c={BULLET_TEXT_COLOR}>
              {b}
            </Text>
          </Group>
        ))}
      </Stack>
    </Stack>
  );
}

export function AiDisclosureModal({
  opened,
  role,
  onAcknowledge,
  onDismiss,
  onReturnToPortal,
}: AiDisclosureModalProps) {
  const copy = COPY_BY_ROLE[role];

  return (
    <Modal
      opened={opened}
      onClose={onDismiss}
      centered
      size={520}
      radius="lg"
      padding="xl"
      withCloseButton
      closeOnClickOutside={false}
      closeOnEscape
      data-testid="ai-disclosure-modal"
      data-role={role}
    >
      <Stack gap="lg">
        {/* MED-1047 — header glyph above a left-aligned title (Figma). */}
        <Stack gap="sm">
          <Box
            aria-hidden="true"
            style={{
              width: 48,
              height: 48,
              borderRadius: 12,
              background: HEADER_ICON_BG,
              color: HEADER_ICON_COLOR,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Mic size={24} />
          </Box>
          <Stack gap={6}>
            <Text fw={700} fz={20} c={TITLE_COLOR} lh={1.3}>
              AI listening is active for this session
            </Text>
            <Text fz={14} c={SUBTITLE_COLOR} lh={1.5}>
              {copy.subtitle}
            </Text>
          </Stack>
        </Stack>

        <DisclosureSection
          icon={<Eye size={16} />}
          title={copy.whatItDoes.title}
          bullets={copy.whatItDoes.bullets}
        />

        <DisclosureSection
          icon={<Users size={16} />}
          title={copy.whatLeaderSees.title}
          bullets={copy.whatLeaderSees.bullets}
        />

        <DisclosureSection
          icon={<Lock size={16} />}
          title={copy.howDataHandled.title}
          bullets={copy.howDataHandled.bullets}
        />

        {/* AC11 — a single divider between the section content and the footer. */}
        <Divider color={DIVIDER_COLOR} />

        <Text fz={12} c={FOOTER_COLOR} lh={1.6}>
          {copy.footer}
        </Text>

        <Stack gap="sm">
          <Button
            fullWidth
            leftSection={<Check size={16} />}
            onClick={onAcknowledge}
            data-testid="ai-disclosure-acknowledge"
            styles={{ root: { backgroundColor: BUTTON_BG } }}
          >
            I understand — continue to session
          </Button>

          {onReturnToPortal && (
            <Button
              fullWidth
              variant="default"
              onClick={onReturnToPortal}
              data-testid="ai-disclosure-return-to-portal"
            >
              Return to portal
            </Button>
          )}
        </Stack>
      </Stack>
    </Modal>
  );
}
